import type { CityConfig } from "@/pages/city-template/types";

export const elkoConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Nevada Vital Records (Birth, Death)", href: "https://dpbh.nv.gov/Programs/VitalRec/", description: "Official Nevada Department of Public Health vital records office in Carson City" },
    { label: "Elko County Clerk — Marriage &amp; Vital Records", href: "https://www.elkocountynv.net/departments/clerk/", description: "Elko County Clerk — marriage licenses and local vital records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Nevada DPBH for online vital record orders" },
  ],
  city: "Elko",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "elko",
  route: "/cities/elko",
  title: "Emergency Passport Elko NV | Expedited Passport Northeastern Nevada",
  metaDescription: "Emergency passport and expedited passport services in Elko, NV — as fast as 24 hours. Fast Passport Center serves Elko, Spring Creek, Carlin, Wells, and northeastern Nevada. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Elko, Nevada",
  heroSubtitle: "Mining industry trip to Australia or Canada coming up fast? Last-minute vacation to Mexico booked from Elko Regional Airport? Family trip to Europe this summer and your passport expired? Fast Passport Center helps Elko and northeastern Nevada residents get expedited passports as fast as 24 hours — entirely remotely. The nearest passport agency is 500+ miles away in San Francisco — we serve you without a 10-hour cross-desert drive.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Elko%20Nevada%20historic%20downtown%20with%20Western%20heritage%20architecture%20and%20cowboy%20culture%20along%20the%20railroad%20corridor%2C%20Ruby%20Mountains%20dramatic%20alpine%20peaks%20rising%20from%20the%20Great%20Basin%20high%20desert%20with%20Lamoille%20Canyon%20scenic%20views%2C%20gold%20mining%20heritage%20and%20Basque%20cultural%20influence%20in%20northeastern%20Nevada%2C%20vast%20sagebrush%20landscape%20under%20wide%20open%20skies%20at%20golden%20hour%2C%20professional%20landscape%20photography%20with%20warm%20earth%20tones%2C%20no%20people%20or%20text%20visible&width=1800&height=700&seq=elko-nv-hero-01&orientation=landscape",
  heroImageAlt: "Elko Nevada downtown with Ruby Mountains at sunset in the Great Basin high desert",
  geoPosition: "40.8324;-115.7631",
  geoPlacename: "Elko, Nevada",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, 3rd Floor, San Francisco, CA 94102",
    distance: "About 530 miles west — approximately 8 hours via I-80. The most remote distance from any passport agency in the contiguous U.S.",
  },
  nearbyCitiesZips: [
    { name: "Elko", zips: "89801" },
    { name: "Spring Creek", zips: "89815" },
    { name: "Carlin", zips: "89822" },
    { name: "Wells", zips: "89835" },
    { name: "West Wendover", zips: "89883" },
    { name: "Battle Mountain", zips: "89820" },
    { name: "Winnemucca", zips: "89445" },
    { name: "Jackpot", zips: "89825" },
    { name: "Lamoille", zips: "89828" },
    { name: "Tuscarora", zips: "89834" },
    { name: "Ruby Valley", zips: "89833" },
    { name: "Jarbidge", zips: "89826" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Elko", desc: "Complete your emergency or expedited passport order from anywhere in northeastern Nevada — you're 8+ hours from the nearest agency, but only minutes from starting online." },
    { icon: "ri-truck-line", title: "Overnight Shipping from Elko", desc: "Ship your documents from The UPS Store in Elko or at Elko Regional Airport — overnight delivery available despite Elko's remote location." },
    { icon: "ri-shield-check-line", title: "Remote Location Expertise", desc: "We understand northeastern Nevada's unique challenges — 500+ miles from any passport agency, limited acceptance facilities, and mining industry travel patterns." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment backlog with reserved San Francisco Passport Agency slots — essential when you're 8+ hours from the nearest agency." },
  ],
  testimonials: [
    {
      name: "James W.",
      location: "Elko, NV",
      rating: 5,
      text: "I'm a geologist at a gold mine and got a last-minute assignment to a mining conference in Perth, Australia with 12 days notice. My passport was expired. Fast Passport Center renewed it in 6 business days from Elko — and we're literally 8 hours from the nearest passport agency. The overnight shipping from The UPS Store in Elko worked perfectly.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Patricia S.",
      location: "Spring Creek, NV",
      rating: 5,
      text: "We booked a family cruise to Alaska out of Seattle — then discovered my husband's passport was expired with three weeks to go. Being in Spring Creek, we're nowhere near any passport agency. Fast Passport Center handled everything remotely. Passport arrived in 5 business days.",
      service: "Emergency Passport Processing",
    },
    {
      name: "Mike T.",
      location: "Elko, NV",
      rating: 5,
      text: "Needed child passports for our kids' school trip to Canada. The Elko Post Office acceptance was smooth, and Fast Passport Center expedited everything from there. Kids' passports arrived in 7 business days. Being in remote northeastern Nevada, we really appreciate a service that works without driving to a big city.",
      service: "Child Passport Expedited",
    },
  ],
  facilities: [
    {
      id: "elko-post-main",
      name: "Elko Main Post Office",
      type: "USPS Passport Acceptance",
      address: "392 5th St",
      city: "Elko",
      state: "NV",
      zip: "89801",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Elko — primary passport acceptance for northeastern Nevada",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility serving Elko County and northeastern Nevada. Appointment required. Availability can be very limited — this is the only acceptance facility within 200+ miles in some directions.",
      icon: "ri-mail-line",
    },
    {
      id: "elko-county-clerk",
      name: "Elko County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "571 Idaho St, Room 103",
      city: "Elko",
      state: "NV",
      zip: "89801",
      phone: "(775) 753-4600",
      hours: "Mon–Fri 8am–5pm",
      distance: "Downtown Elko — Elko County Courthouse",
      website: "https://www.elkocountynv.net/departments/clerk/index.php",
      websiteLabel: "Elko County Clerk",
      notes: "Elko County Clerk accepts passport applications at the Courthouse. Important backup option for Elko County residents — often better availability than the post office.",
      icon: "ri-government-line",
    },
    {
      id: "spring-creek-post",
      name: "Spring Creek Post Office",
      type: "USPS Passport Acceptance",
      address: "776 Spring Creek Pkwy",
      city: "Spring Creek",
      state: "NV",
      zip: "89815",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Spring Creek — about 10 miles south of Elko",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative acceptance facility for Spring Creek and south Elko County residents. Very limited appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "wells-post",
      name: "Wells Post Office",
      type: "USPS Passport Acceptance",
      address: "700 6th St",
      city: "Wells",
      state: "NV",
      zip: "89835",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Wells — about 50 miles east of Elko via I-80",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Small facility serving east Elko County and the I-80 corridor. Very limited availability — Elko strongly preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "battle-mountain-post",
      name: "Battle Mountain Post Office",
      type: "USPS Passport Acceptance",
      address: "650 W Front St",
      city: "Battle Mountain",
      state: "NV",
      zip: "89820",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Battle Mountain — about 70 miles west of Elko via I-80",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative for west Elko County and Lander County residents. Small rural facility with limited hours.",
      icon: "ri-mail-line",
    },
    {
      id: "winnemucca-post",
      name: "Winnemucca Post Office",
      type: "USPS Passport Acceptance",
      address: "850 Hanson St",
      city: "Winnemucca",
      state: "NV",
      zip: "89445",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Winnemucca — about 125 miles west of Elko via I-80",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Largest acceptance facility in northwestern Nevada. Alternative for west Elko County residents willing to make the drive.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Elko, Nevada?",
      a: "Yes. Fast Passport Center handles emergency passport processing for Elko residents as fast as 24 hours. The San Francisco Passport Agency is about 530 miles from Elko (8+ hours driving) — among the farthest distances from a passport agency anywhere in the U.S. We serve you entirely remotely via overnight shipping from The UPS Store in Elko.",
    },
    {
      q: "How fast can I get an expedited passport from Elko, NV?",
      a: "Expedited passport processing from Elko takes 2–9 business days through Fast Passport Center. Given Elko's extreme distance from any passport agency, courier service is not just convenient — it's practically essential for any expedited passport need.",
    },
    {
      q: "Where is the passport acceptance office in Elko?",
      a: "Elko's passport acceptance locations are the Elko Main Post Office (392 5th St) and the Elko County Clerk's Office (571 Idaho St). These serve all of northeastern Nevada — the next nearest acceptance facilities are 70+ miles away in Battle Mountain or 125 miles in Winnemucca.",
    },
    {
      q: "Is Elko really 500+ miles from the nearest passport agency?",
      a: "Yes. Elko is approximately 530 miles from the San Francisco Passport Agency — about 8 hours driving via I-80. This makes Elko one of the most remote locations from a passport agency in the contiguous United States. Courier service is the only practical expedited option for northeastern Nevada travelers.",
    },
    {
      q: "Does the mining industry create passport demand in Elko?",
      a: "Yes — Elko is the heart of Nevada's gold mining industry. Geologists, mining engineers, and executives travel internationally to mining conferences and operations in Australia (Perth, Kalgoorlie), Canada (Toronto, Vancouver, Yukon), Chile, Peru, South Africa, and Ghana. These trips often come with 2–6 weeks notice.",
    },
    {
      q: "What airport do Elko residents use for international travel?",
      a: "Elko Regional Airport (EKO) offers limited regional service to Salt Lake City (SLC). For international flights, most Elko travelers drive to Salt Lake City International (230 miles, 3.5 hours) or fly from Elko to SLC and connect. Reno-Tahoe International (RNO) is 290 miles west via I-80.",
    },
    {
      q: "Does Elko's Basque heritage create international travel patterns?",
      a: "Elko is home to a significant Basque-American community and hosts the National Basque Festival. Many Elko residents travel to the Basque Country in Spain and France for heritage visits, family reunions, and cultural exchanges — often planned months in advance.",
    },
    {
      q: "Can I renew my passport from Elko without visiting an office?",
      a: "Yes. Most adult Elko residents renewing their passport can do so entirely by mail — no in-person acceptance visit required for renewals. Ship overnight from The UPS Store Elko, and we handle the San Francisco agency submission remotely.",
    },
    {
      q: "Can I get a child passport from Elko?",
      a: "Yes. Child passports require an in-person acceptance visit — Elko families use the Elko Main Post Office or Elko County Clerk's Office. Both parents must typically be present or provide notarized consent.",
    },
    {
      q: "How much does expedited passport processing cost from Elko?",
      a: "Fast Passport Center pricing includes the U.S. government passport fees plus our courier service fee — which is typically 30–100% lower than FedEx Office or retail passport services. We provide an exact price quote at checkout before any commitment.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Elko", address: "2575 Mountain City Hwy, Elko, NV 89801", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com" },
    { name: "The UPS Store — Elko", address: "2572 Idaho St, Elko, NV 89801", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 10 AM – 3 PM", website: "https://www.theupsstore.com" },
    { name: "Walmart Supercenter — Elko", address: "2944 Mountain City Hwy, Elko, NV 89801", hours: "Daily 6 AM – 11 PM", website: "https://www.walmart.com" },
    { name: "Elko Main Post Office", address: "392 5th St, Elko, NV 89801", hours: "Mon–Fri 9 AM – 5 PM", website: "https://tools.usps.com/find-location.htm" },
    { name: "Smith's Food & Drug — Elko", address: "1855 W Wendover Blvd, Elko, NV 89801", hours: "Daily 6 AM – 11 PM", website: "https://www.smithsfoodanddrug.com" },
    { name: "CVS Pharmacy — Salt Lake City (airport area)", address: "multiple locations near SLC airport", hours: "Most open 24 hours", website: "https://www.cvs.com" },
    { name: "FedEx Office — Salt Lake City", address: "various locations in Salt Lake City, UT", hours: "Mon–Fri 7 AM – 10 PM", website: "https://www.office.fedex.com" },
    { name: "Walgreens — Spring Creek", address: "near Spring Creek Pkwy, Spring Creek, NV 89815", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com" },
  ],
  dmvLocations: [
    {
      name: "Elko DMV",
      address: "3920 E Idaho St, Elko, NV 89801",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Elko DMV — driver licenses, REAL ID, and state ID cards. Primary DMV serving all of northeastern Nevada.",
      featured: true,
    },
    {
      name: "Winnemucca DMV",
      address: "3505 Construction Way, Winnemucca, NV 89445",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Winnemucca DMV — about 125 miles west of Elko via I-80. Alternative for west Elko County residents.",
      featured: false,
    },
    {
      name: "Salt Lake City DMV (Utah)",
      address: "multiple locations in Salt Lake City, UT",
      hours: "Varies by location",
      note: "Utah DMV — some Elko residents use Utah DMV when already traveling to Salt Lake City. Note: different state, different requirements.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "EKO",
      name: "Elko Regional Airport",
      distance: "Local regional airport — about 3 miles east of downtown Elko",
      address: "975 Terminal Dr, Elko, NV 89801",
      note: "Elko Regional Airport offers limited regional service to Salt Lake City (SLC) via Delta Connection. No international flights — connect through SLC for all international travel.",
      flightUrl: "https://www.elkocounty.org/airport",
      flightLabel: "EKO Airport Website",
      featured: true,
    },
    {
      code: "SLC",
      name: "Salt Lake City International Airport",
      distance: "Primary international gateway — about 230 miles east via I-80 (approximately 3.5 hours)",
      address: "776 N Terminal Dr, Salt Lake City, UT 84122",
      note: "Salt Lake City International is the primary international hub for Elko travelers. SLC offers direct flights to Europe (London, Paris, Amsterdam), Asia (Tokyo), Mexico, and Canada. Most Elko residents either drive or take the EKO-SLC connection flight.",
      flightUrl: "https://www.slcairport.com/",
      flightLabel: "SLC Airport Website",
      featured: true,
    },
    {
      code: "RNO",
      name: "Reno-Tahoe International Airport",
      distance: "About 290 miles west via I-80 (approximately 4.5 hours)",
      address: "2001 E Plumb Ln, Reno, NV 89502",
      note: "Alternative regional hub. RNO connects to major gateways (SFO, LAX, DEN, SEA) but is farther from Elko than SLC.",
      flightUrl: "https://www.renoairport.com/",
      flightLabel: "RNO Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Elko & Northeastern Nevada",
      description: "Mining conference in Australia departing in days? Family emergency overseas? Elko is 8+ hours from the nearest passport agency — Fast Passport Center handles emergency passport processing remotely, as fast as 24 hours.",
      bullets: ["As fast as 24 hours processing", "Reserved San Francisco agency submission", "Dedicated agent and live status tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired and you live in one of the most remote locations from a passport agency in the U.S.? Elko residents renew fast through Fast Passport Center — ship overnight from The UPS Store Elko.",
      bullets: ["Fastest renewal for northeastern Nevada", "Ship overnight from Elko", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in Elko or before an international mining assignment? We guide northeastern Nevada travelers through DS-64 and expedited replacement — no 8-hour drive to San Francisco.",
      bullets: ["DS-64 theft/loss reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Elko Families",
      description: "Elko families heading to Canada, Mexico, or European vacations (including Basque Country heritage trips) need child passports. We guide you through the Elko Post Office or County Clerk acceptance step.",
      bullets: ["Both parents typically required to consent", "Elko Post Office or County Clerk guidance", "Expedited after in-person acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from Elko? In-person acceptance at the Elko Post Office or County Clerk is required for first-timers. We guide you through every step — especially important given Elko's remote location.",
      bullets: ["Step-by-step acceptance guidance for Elko facilities", "Photo location guide for Elko and northeastern Nevada", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices than retail services. Essential for remote locations like Elko.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change passports, damaged passport replacement, second passports for mining industry professionals, and Elko and northeastern Nevada residents with complex passport needs.",
      bullets: ["Name change after marriage", "Damaged passport replacement", "Second passport for mining professionals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Elko, Nevada residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for Elko and northeastern Nevada residents in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for Elko, Nevada — ship overnight from The UPS Store Elko." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Elko travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Elko and Elko County families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Elko, Nevada and northeastern Nevada." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking International Mining Conferences Without Checking Passport Validity", description: "Elko mining professionals frequently book international conferences in Australia, Canada, and South America months in advance but discover expired passports 2–3 weeks before departure." },
    { icon: "ri-camera-off-line", title: "Submitting Poor-Quality Passport Photos", description: "With limited photo options in rural Nevada, photos from drugstores can get rejected. Fast Passport Center reviews photo specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — a disaster when you're 500+ miles from the nearest agency. Our pre-submission review catches all errors." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are rejected. We specify exactly what document format is needed based on your state of birth." },
    { icon: "ri-map-pin-time-line", title: "Underestimating Elko's Extreme Distance from Passport Agencies", description: "Elko is 530 miles (8+ hours) from the San Francisco Passport Agency — one of the most remote distances in the country. Don't plan to 'just drive to the agency' — use Fast Passport Center's remote service." },
    { icon: "ri-global-line", title: "Mining Industry R&R International Travel — Last-Minute Passport Discovery", description: "Mining professionals on FIFO schedules often book international vacations during R&R breaks and discover passport issues at the last minute. Check passport validity during every R&R cycle." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Elko travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with Elko acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight", description: "Drop off at The UPS Store in Elko for overnight delivery. Despite Elko's remote location, overnight shipping is available." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved San Francisco Passport Agency slots — bypassing the public appointment backlog." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Door", description: "Your completed passport ships directly to your Elko or Elko County address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Not Practical from Elko",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to San Francisco — 8+ hours (530 miles)",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Essentially impractical from northeastern Nevada",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from Elko",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail from rural Nevada — even slower",
        "6–13 weeks; can be longer from remote locations",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait, San Francisco only (530 miles)", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive to San Francisco — 8+ hours from Elko, essentially impractical", fpc: "Ship overnight from Elko" },
    { feature: "Processing Speed", agency: "Strict eligibility requirements", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Remote Nevada Accessibility", agency: "No practical access from northeastern Nevada", fpc: "Fully remote — designed for remote locations" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 16-hour SF round trip + hotel and meals", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Reno", route: "/cities/reno" },
    { name: "Emergency passport help in Winnemucca", route: "/cities/winnemucca" },
    { name: "Fast passport renewal for Nevada residents", route: "/states/nevada" },
    { name: "Rush passport services in Spring Creek", route: "/cities/spring-creek" },
    { name: "Expedited passports statewide in Nevada", route: "/states/nevada" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from The UPS Store in Elko — available weekday evenings. Despite Elko's remote location, overnight shipping is available. No visit to our processing facility required.",
  fpcOfficeDistance: "Serving Elko, Spring Creek, and all of northeastern Nevada remotely — we're designed for America's most remote communities",
  serviceAreaIntro: "Whether you are in Elko, Spring Creek, Carlin, Wells, West Wendover, or anywhere in northeastern Nevada — Fast Passport Center makes emergency and expedited passport processing simple despite being 500+ miles from the nearest passport agency.",
  nearbyCitiesIntro: "We serve all Elko County zip codes and surrounding communities including Spring Creek, Carlin, Wells, West Wendover, Battle Mountain, Winnemucca, Jackpot, and Lamoille.",
  howItWorksIntro: "Getting an emergency or expedited passport from Elko is simpler than an 8+ hour drive to San Francisco. Most northeastern Nevada clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Elko and northeastern Nevada passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from Elko, Spring Creek, and northeastern Nevada travelers who needed their passports fast.",
  faqIntro: "The most common questions Elko and Elko County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  travelTiming: {
    title: "Travel & Passport Timing for Elko and Northeastern Nevada Residents",
    intro: "Elko is the heart of Nevada's gold mining country — home to the Ruby Mountains, a historic Basque community, and the largest gold-producing region in the United States. Its extreme remoteness (500+ miles from any passport agency) and mining-driven economy create some of the most distinctive international travel patterns in the country.",
    cards: [
      {
        icon: "ri-hammer-line",
        title: "Gold Mining Industry International Travel",
        subtitle: "Elko's economic engine drives global passport needs",
        description: "Elko's gold mining industry — including operations by Barrick, Newmont, and Kinross — sends geologists, engineers, executives, and technicians internationally on a regular basis. Common destinations include Perth and Kalgoorlie (Australia gold mining), Toronto and Vancouver (mining finance and conferences), Santiago (Chile copper/gold), Lima (Peru), and Johannesburg (South Africa). Mining conferences like PDAC in Toronto and Diggers & Dealers in Kalgoorlie draw Elko mining professionals annually.",
        tip: "Mining professionals should maintain a passport with 12+ months validity at all times. Mining conference dates are fixed — PDAC (Toronto, March) and Diggers & Dealers (Kalgoorlie, August) are annual events that require passports.",
      },
      {
        icon: "ri-earth-line",
        title: "Basque Heritage and European Travel",
        subtitle: "Elko's unique Basque community creates transatlantic connections",
        description: "Elko is home to one of the largest Basque-American communities in the U.S. and hosts the National Basque Festival each summer. Many Elko residents maintain strong connections to the Basque Country (Euskal Herria) spanning northern Spain and southwestern France. Heritage trips, family reunions, and cultural exchanges drive regular transatlantic travel from Elko to Bilbao, San Sebastian, Pamplona, and Biarritz.",
        tip: "Basque Country travel is typically planned months in advance. Check passport validity when booking flights — Schengen Area requirements mean passports must be valid for at least 3 months beyond your planned departure from Europe.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "Salt Lake City Gateway Travel",
        subtitle: "Elko's connection to international flights through SLC",
        description: "Elko Regional Airport (EKO) connects to Salt Lake City (SLC) via Delta Connection. Salt Lake City International is a major Delta hub with direct international flights to London, Paris, Amsterdam, Tokyo, Mexico City, and Vancouver. Most Elko residents either fly EKO-SLC or drive 3.5 hours to SLC for international departures.",
        tip: "When booking international flights through SLC, allow extra time for the EKO-SLC connection (or drive). Winter weather on I-80 can add significant time. Consider flying to SLC the day before an international departure.",
      },
      {
        icon: "ri-sun-line",
        title: "R&R International Vacations — the FIFO Pattern",
        subtitle: "Mining R&R cycles shape vacation travel timing",
        description: "Many Elko mining professionals work fly-in-fly-out (FIFO) or rotational schedules. During R&R breaks, international vacations to Mexico, Hawaii, the Caribbean, and Europe are common. Passport issues are frequently discovered during the R&R period when travel is already being planned — creating 1–2 week urgency windows.",
        tip: "Check passport validity during every R&R cycle — not just when booking a trip. Maintaining a current passport means you're always ready when international vacation opportunities arise during your break.",
      },
    ],
    callout: {
      icon: "ri-hammer-line",
      title: "Elko, Nevada — The Heart of American Gold Country",
      description: "Elko sits at the intersection of gold mining heritage, Basque culture, and the wide-open Great Basin. Its residents — from mining engineers to ranchers to Basque community leaders — create an unusual international travel profile that spans Australian gold fields, Basque Country villages, and Canadian mining finance centers. Fast Passport Center has served northeastern Nevada residents with expedited passports for mining conferences, heritage trips, and remote-location international travel.",
    },
  },
  destinations: {
    title: "Where Elko Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Elko and northeastern Nevada clients. Gold mining conferences, Basque heritage travel, SLC gateway connections, and FIFO vacation patterns create some of the most distinctive passport urgency situations in the country.",
    stats: [
      { stat: "530 mi", label: "to San Francisco Passport Agency — one of the farthest distances from any agency in the U.S." },
      { stat: "230 mi", label: "to Salt Lake City International (SLC) — primary international gateway for Elko travelers" },
      { stat: "8+ hrs", label: "to the nearest passport agency — making courier service essential, not optional" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from Elko" },
    ],
    destinations: [
      { name: "Australia — Perth, Kalgoorlie, Brisbane", context: "Gold mining conferences, technical exchanges, and corporate meetings in Australia's mining regions", urgency: "Mining conferences scheduled 6–8 weeks out; FIFO professionals need fast turnaround", icon: "ri-earth-line" },
      { name: "Canada — Toronto, Vancouver, Calgary", context: "Mining finance (PDAC conference), corporate meetings, and cross-border business travel", urgency: "PDAC in Toronto is annual (March); passports needed with 4–8 weeks notice", icon: "ri-building-2-line" },
      { name: "Basque Country — Spain & France", context: "Heritage trips, family reunions, and cultural exchanges to Euskal Herria from Elko's Basque community", urgency: "Summer heritage trips planned January–March; Schengen Area passport validity rules apply", icon: "ri-global-line" },
      { name: "Mexico — Beach & Resort Vacations", context: "R&R vacation travel from mining professionals and families escaping Nevada winters", urgency: "Booked during R&R cycles; 2–4 weeks before departure — peak expedited demand", icon: "ri-sun-cloudy-line" },
      { name: "South America — Chile, Peru", context: "Mining operations and technical exchanges at South American gold and copper mines", urgency: "Technical site visits confirmed 3–6 weeks out; remote passport processing essential", icon: "ri-ship-line" },
      { name: "Europe — London, Paris, Amsterdam", context: "Combined business-leisure trips through SLC's European routes — popular with Elko professionals", urgency: "Booked 4–12 weeks out; summer peak creates predictable June–July demand", icon: "ri-flight-takeoff-line" },
    ],
    crossLinksTitle: "Explore Nearby Nevada Service Areas",
    crossLinksDesc: "We serve travelers throughout northeastern Nevada and the I-80 corridor with the same expedited passport expertise.",
    crossLinks: [
      { name: "Reno", route: "/cities/reno" },
      { name: "Las Vegas", route: "/cities/las-vegas" },
      { name: "Winnemucca", route: "/cities/winnemucca" },
    ],
  },
};